import { useEffect, useState } from 'react';

import { useFlags } from 'shared/components/common/feature_flags/useFlags';

import config from 'config';

interface SetupStatus {
  acceptingCooOrders: boolean
  shortUrl?: string,
}

interface SetupStatusMap {
  [restaurantGuid: string]: SetupStatus
}

interface SetupStatusResponse {
  cooSetupStatuses: SetupStatusMap
}

interface CateringLinkMap {
  [key: string]: string | undefined
}

const urlForLookupByRx = (restaurantGuid: string) => {
  return `${config.resources.apiHost}/catering/v2/public/restaurant/setup-status/${restaurantGuid}`;
};

const urlForLookupByGroup = (mgmtGroupGuid: string) => {
  return `${config.resources.apiHost}/catering/v2/public/management-set/setup-status/${mgmtGroupGuid}`;
};

const fetchCateringSetupStatusMap = async (url: string): Promise<SetupStatusResponse> => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return await response.json();
  } catch(err) {
    return { cooSetupStatuses: {} };
  }
};

const getCateringOOLinkMap = async (url: string, utmSource: string) => {
  const response = await fetchCateringSetupStatusMap(url);
  const linkMap: CateringLinkMap = {};

  // The catering API calculates a few things (whether or not the rx has COO, if it's
  // on and fully set up, etc) which is why its the condition to setting the url.
  Object.entries(response.cooSetupStatuses).forEach(([restaurantGuid, setupStatus]) => {
    const showCateringLink = setupStatus.acceptingCooOrders;
    const cateringLink = showCateringLink ? setupStatus.shortUrl : undefined;

    linkMap[restaurantGuid] = getCateringOOFullUrl(cateringLink, utmSource);
  });

  return linkMap;
};


const getCateringOOFullUrl = (shortUrl: string | undefined, utmSource: string) => {
  if(!shortUrl) return undefined;
  return `${config.resources.toastwebBaseUri}catering/${shortUrl}?utm_source=${utmSource}`;
};

export const useCateringSetupStatusByGroup = (managementGuid: string | null, utmSource: string) => {
  const [cateringLinkMap, setCateringLinkMap] = useState<CateringLinkMap>({});
  const { nvsCateringToastLocal } = useFlags();

  useEffect(() => {
    if(managementGuid && nvsCateringToastLocal) {
      const url = urlForLookupByGroup(managementGuid);
      getCateringOOLinkMap(url, utmSource)
        .then(setCateringLinkMap)
        .catch(_ => {
          setCateringLinkMap({});
        });
    }
  }, [managementGuid, nvsCateringToastLocal, utmSource]);

  return cateringLinkMap;
};

export const useCateringSetupStatusByRx = (restaurantGuid: string | null, utmSource: string) => {
  const [cateringLink, setCateringLink] = useState<string>();
  const { nvsCateringToastLocal } = useFlags();

  useEffect(() => {
    if(restaurantGuid && nvsCateringToastLocal) {
      const url = urlForLookupByRx(restaurantGuid);
      getCateringOOLinkMap(url, utmSource)
        .then(resp => setCateringLink(resp[restaurantGuid]))
        .catch(_ => {
          setCateringLink(undefined);
        });
    }
  }, [restaurantGuid, nvsCateringToastLocal, utmSource]);

  return cateringLink;
};
